import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import axios from 'axios';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { API } from '../App';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import { Helmet } from 'react-helmet';
import AdvancedFilters from '../components/AdvancedFilters';
import FilterChips from '../components/FilterChips';
import { generatePropertySchema, injectSchema, removeSchema } from '../utils/schemaGenerator';

// Constants
const ITEMS_PER_PAGE = 15;
const DEBOUNCE_DELAY = 400;
const REQUEST_TIMEOUT = 15000;
const CACHE_TTL = 5 * 60 * 1000; // 5 minutes

// Search cache for client-side caching
const searchCache = new Map();

// Generate cache key from search params
const getCacheKey = (filters, page) => {
  return JSON.stringify({ ...filters, page });
};

// Check if cache is valid
const isCacheValid = (cacheEntry) => {
  return cacheEntry && (Date.now() - cacheEntry.timestamp) < CACHE_TTL;
};

// Skeleton Loading Component
const PropertySkeleton = () => (
  <div className="bg-white rounded-lg shadow-sm overflow-hidden animate-pulse">
    <div className="aspect-video bg-gray-200" />
    <div className="p-4 space-y-3">
      <div className="h-4 bg-gray-200 rounded w-3/4" />
      <div className="h-6 bg-gray-200 rounded w-1/2" />
      <div className="flex gap-2">
        <div className="h-4 bg-gray-200 rounded w-16" />
        <div className="h-4 bg-gray-200 rounded w-16" />
        <div className="h-4 bg-gray-200 rounded w-16" />
      </div>
      <div className="h-4 bg-gray-200 rounded w-full" />
    </div>
  </div>
);

const PropertySearch = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const abortControllerRef = useRef(null);
  const debounceTimerRef = useRef(null);
  
  // State
  const [listings, setListings] = useState([]);
  const [loading, setLoading] = useState(false);
  const [initialLoad, setInitialLoad] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [totalResults, setTotalResults] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [hasMore, setHasMore] = useState(false);
  const [error, setError] = useState(null);
  const [hasSearched, setHasSearched] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  
  // Detail modal state
  const [selectedListing, setSelectedListing] = useState(null);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [showReportModal, setShowReportModal] = useState(false);
  const [reportData, setReportData] = useState({ reason: '', description: '', reporterEmail: '' });
  
  // Filters state
  const [filters, setFilters] = useState({
    province: searchParams.get('province') || '',
    city: searchParams.get('city') || '',
    suburb: searchParams.get('suburb') || '',
    listingType: searchParams.get('type') || '',
    propertyTypes: searchParams.get('propertyType')?.split(',').filter(Boolean) || [],
    minPrice: searchParams.get('minPrice') ? parseInt(searchParams.get('minPrice')) : null,
    maxPrice: searchParams.get('maxPrice') ? parseInt(searchParams.get('maxPrice')) : null,
    minBeds: searchParams.get('beds') ? parseInt(searchParams.get('beds')) : null,
    minBaths: searchParams.get('baths') ? parseInt(searchParams.get('baths')) : null,
    minSize: searchParams.get('minSize') ? parseInt(searchParams.get('minSize')) : null,
    maxSize: searchParams.get('maxSize') ? parseInt(searchParams.get('maxSize')) : null,
    amenities: searchParams.get('amenities')?.split(',').filter(Boolean) || [],
    sortBy: searchParams.get('sort') || 'newest'
  });

  // Check for mobile
  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
    };
  }, []);

  // Parse URL params on mount - trigger search if params exist
  useEffect(() => {
    const hasUrlFilters = searchParams.get('province') || searchParams.get('city') || 
                          searchParams.get('type') || searchParams.get('propertyType');
    
    if (hasUrlFilters) {
      const newFilters = {
        province: searchParams.get('province') || '',
        city: searchParams.get('city') || '',
        suburb: searchParams.get('suburb') || '',
        listingType: searchParams.get('type') || '',
        propertyTypes: searchParams.get('propertyType')?.split(',').filter(Boolean) || [],
        minPrice: searchParams.get('minPrice') ? parseInt(searchParams.get('minPrice')) : null,
        maxPrice: searchParams.get('maxPrice') ? parseInt(searchParams.get('maxPrice')) : null,
        minBeds: searchParams.get('beds') ? parseInt(searchParams.get('beds')) : null,
        minBaths: searchParams.get('baths') ? parseInt(searchParams.get('baths')) : null,
        minSize: searchParams.get('minSize') ? parseInt(searchParams.get('minSize')) : null,
        maxSize: searchParams.get('maxSize') ? parseInt(searchParams.get('maxSize')) : null,
        amenities: searchParams.get('amenities')?.split(',').filter(Boolean) || [],
        sortBy: searchParams.get('sort') || 'newest'
      };
      setFilters(newFilters);
      const page = parseInt(searchParams.get('page')) || 1;
      setCurrentPage(page);
      // Auto-search if URL has filters
      searchListings(page, newFilters);
    }
    setInitialLoad(false);
  }, []);

  // Build SEO-friendly URL from filters
  const buildSearchUrl = useCallback((newFilters, page = 1) => {
    const params = new URLSearchParams();
    
    if (newFilters.province) params.set('province', newFilters.province);
    if (newFilters.city) params.set('city', newFilters.city);
    if (newFilters.suburb) params.set('suburb', newFilters.suburb);
    if (newFilters.listingType) params.set('type', newFilters.listingType);
    if (newFilters.propertyTypes?.length) params.set('propertyType', newFilters.propertyTypes.join(','));
    if (newFilters.minPrice) params.set('minPrice', newFilters.minPrice);
    if (newFilters.maxPrice && newFilters.maxPrice < 50000000) params.set('maxPrice', newFilters.maxPrice);
    if (newFilters.minBeds) params.set('beds', newFilters.minBeds);
    if (newFilters.minBaths) params.set('baths', newFilters.minBaths);
    if (newFilters.minSize) params.set('minSize', newFilters.minSize);
    if (newFilters.maxSize) params.set('maxSize', newFilters.maxSize);
    if (newFilters.amenities?.length) params.set('amenities', newFilters.amenities.join(','));
    if (newFilters.sortBy && newFilters.sortBy !== 'newest') params.set('sort', newFilters.sortBy);
    if (page > 1) params.set('page', page);
    
    return params.toString();
  }, []);

  // Search listings with caching, timeout, and error handling
  const searchListings = useCallback(async (page = 1, overrideFilters = null) => {
    const activeFilters = overrideFilters || filters;
    const cacheKey = getCacheKey(activeFilters, page);
    
    // Check cache first
    const cachedResult = searchCache.get(cacheKey);
    if (isCacheValid(cachedResult)) {
      setListings(cachedResult.data.listings);
      setTotalResults(cachedResult.data.total);
      setTotalPages(cachedResult.data.totalPages);
      setHasMore(cachedResult.data.hasMore);
      setCurrentPage(page);
      setHasSearched(true);
      setError(null);
      
      // Update URL
      const urlParams = buildSearchUrl(activeFilters, page);
      navigate(`/properties${urlParams ? '?' + urlParams : ''}`, { replace: true });
      return;
    }
    
    // Cancel any pending requests
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
    abortControllerRef.current = new AbortController();
    
    try {
      setLoading(true);
      setError(null);
      
      const response = await axios.post(`${API}/listings/search`, {
        province: activeFilters.province || null,
        city: activeFilters.city || null,
        suburb: activeFilters.suburb || null,
        propertyType: activeFilters.propertyTypes?.length > 0 ? activeFilters.propertyTypes : null,
        listingType: activeFilters.listingType || null,
        minPrice: activeFilters.minPrice || null,
        maxPrice: activeFilters.maxPrice || null,
        minBeds: activeFilters.minBeds || null,
        minBaths: activeFilters.minBaths || null,
        minSize: activeFilters.minSize || null,
        maxSize: activeFilters.maxSize || null,
        amenities: activeFilters.amenities?.length > 0 ? activeFilters.amenities : null,
        sortBy: activeFilters.sortBy || 'newest',
        page: page,
        limit: ITEMS_PER_PAGE
      }, {
        signal: abortControllerRef.current.signal,
        timeout: REQUEST_TIMEOUT
      });
      
      // Cache the result
      searchCache.set(cacheKey, {
        data: response.data,
        timestamp: Date.now()
      });
      
      // Limit cache size
      if (searchCache.size > 50) {
        const oldestKey = searchCache.keys().next().value;
        searchCache.delete(oldestKey);
      }
      
      setListings(response.data.listings);
      setTotalResults(response.data.total);
      setTotalPages(response.data.totalPages);
      setHasMore(response.data.hasMore);
      setCurrentPage(page);
      setHasSearched(true);
      
      // Update URL
      const urlParams = buildSearchUrl(activeFilters, page);
      navigate(`/properties${urlParams ? '?' + urlParams : ''}`, { replace: true });
      
    } catch (error) {
      if (axios.isCancel(error)) {
        // Request was cancelled, ignore
        return;
      }
      
      console.error('Error searching listings:', error);
      
      if (error.code === 'ECONNABORTED' || error.message?.includes('timeout')) {
        setError('Search timed out. Please try again with fewer filters.');
        toast.error('Search timed out. Please try again.');
      } else if (error.response?.status >= 500) {
        setError('Server error. Please try again later.');
        toast.error('Server error. Please try again later.');
      } else {
        setError('Failed to load properties. Please try again.');
        toast.error('Failed to load properties');
      }
      
      setListings([]);
      setTotalResults(0);
      setHasSearched(true);
    } finally {
      setLoading(false);
    }
  }, [filters, navigate, buildSearchUrl]);

  // Debounced search for input changes
  const debouncedSearch = useCallback((newFilters) => {
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }
    
    debounceTimerRef.current = setTimeout(() => {
      searchListings(1, newFilters);
    }, DEBOUNCE_DELAY);
  }, [searchListings]);

  // Handle filter changes
  const handleFiltersChange = (newFilters) => {
    setFilters(newFilters);
  };

  // Handle search button click
  const handleSearch = () => {
    setCurrentPage(1);
    searchListings(1);
  };

  // Handle quick search with debounce
  const handleQuickSearch = (e) => {
    const value = e.target.value;
    setSearchQuery(value);
    
    if (value.length >= 2) {
      const newFilters = { ...filters, city: value };
      setFilters(newFilters);
      debouncedSearch(newFilters);
    }
  };

  // Handle quick search on Enter
  const handleQuickSearchKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
      const newFilters = { ...filters, city: searchQuery };
      setFilters(newFilters);
      searchListings(1, newFilters);
    }
  };

  // Handle clear filters
  const handleClearFilters = () => {
    const clearedFilters = {
      province: '',
      city: '',
      suburb: '',
      listingType: '',
      propertyTypes: [],
      minPrice: null,
      maxPrice: null,
      minBeds: null,
      minBaths: null,
      minSize: null,
      maxSize: null,
      amenities: [],
      sortBy: 'newest'
    };
    setFilters(clearedFilters);
    setSearchQuery('');
    setListings([]);
    setTotalResults(0);
    setHasSearched(false);
    setError(null);
    navigate('/properties', { replace: true });
  };

  // Handle page change
  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= totalPages && newPage !== currentPage) {
      setCurrentPage(newPage);
      searchListings(newPage);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  // Remove a specific filter
  const handleRemoveFilter = (filterKey, value) => {
    let newFilters = { ...filters };
    
    if (filterKey === 'propertyTypes' || filterKey === 'amenities') {
      newFilters[filterKey] = newFilters[filterKey].filter(v => v !== value);
    } else {
      newFilters[filterKey] = filterKey.includes('Price') || filterKey.includes('Beds') || 
                              filterKey.includes('Baths') || filterKey.includes('Size') ? null : '';
    }
    
    setFilters(newFilters);
    if (hasSearched) {
      searchListings(1, newFilters);
    }
  };

  // Handle listing click for detail modal
  const handleListingClick = (listing) => {
    setSelectedListing(listing);
    setCurrentImageIndex(0);
    setShowDetailModal(true);
    
    if (listing) {
      const schema = generatePropertySchema(listing);
      if (schema) injectSchema(schema);
    }
  };

  // Close detail modal
  const closeDetailModal = () => {
    setShowDetailModal(false);
    setSelectedListing(null);
    removeSchema();
  };

  // Handle image navigation
  const handleNextImage = () => {
    if (selectedListing?.images?.length > 0) {
      setCurrentImageIndex((prev) => (prev + 1) % selectedListing.images.length);
    }
  };

  const handlePrevImage = () => {
    if (selectedListing?.images?.length > 0) {
      setCurrentImageIndex((prev) => (prev - 1 + selectedListing.images.length) % selectedListing.images.length);
    }
  };

  // Handle report submission
  const handleReportSubmit = async () => {
    if (!reportData.reason || !reportData.reporterEmail) {
      toast.error('Please fill in all required fields');
      return;
    }
    
    try {
      await axios.post(`${API}/reports`, {
        ...reportData,
        listingId: selectedListing?.id,
        listingTitle: selectedListing?.title
      });
      toast.success('Report submitted successfully');
      setShowReportModal(false);
      setReportData({ reason: '', description: '', reporterEmail: '' });
    } catch (error) {
      toast.error('Failed to submit report');
    }
  };

  // Format price
  const formatPrice = (price) => {
    return new Intl.NumberFormat('en-ZA', {
      style: 'currency',
      currency: 'ZAR',
      maximumFractionDigits: 0
    }).format(price);
  };

  // Count active filters
  const activeFilterCount = useMemo(() => {
    let count = 0;
    if (filters.province) count++;
    if (filters.city) count++;
    if (filters.suburb) count++;
    if (filters.listingType) count++;
    if (filters.propertyTypes?.length > 0) count += filters.propertyTypes.length;
    if (filters.minPrice) count++;
    if (filters.maxPrice && filters.maxPrice < 50000000) count++;
    if (filters.minBeds) count++;
    if (filters.minBaths) count++;
    if (filters.minSize) count++;
    if (filters.maxSize) count++;
    if (filters.amenities?.length > 0) count += filters.amenities.length;
    return count;
  }, [filters]);

  // Generate page title
  const pageTitle = useMemo(() => {
    const parts = [];
    if (filters.listingType === 'sale') parts.push('Properties for Sale');
    else if (filters.listingType === 'rental') parts.push('Properties to Rent');
    else parts.push('Find Properties');
    
    if (filters.city) parts.push(`in ${filters.city}`);
    else if (filters.province) parts.push(`in ${filters.province.replace(/-/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}`);
    
    return parts.join(' ') + ' | Flippy Homes';
  }, [filters]);

  // Render skeleton grid
  const renderSkeletons = () => (
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6">
      {[...Array(6)].map((_, i) => (
        <PropertySkeleton key={i} />
      ))}
    </div>
  );

  // Render empty state
  const renderEmptyState = () => (
    <div className="text-center py-16" data-testid="empty-results">
      <div className="w-20 h-20 mx-auto mb-6 bg-gray-100 rounded-full flex items-center justify-center">
        <i className="fas fa-home text-3xl text-gray-400"></i>
      </div>
      <h3 className="text-xl font-semibold text-gray-700 mb-2">No properties found</h3>
      <p className="text-gray-500 mb-6 max-w-md mx-auto">
        {activeFilterCount > 0 
          ? "Try adjusting your filters or search criteria to find more properties."
          : "Click 'Search Properties' to find available listings."}
      </p>
      {activeFilterCount > 0 && (
        <Button 
          variant="outline" 
          onClick={handleClearFilters}
          data-testid="clear-filters-btn"
        >
          <i className="fas fa-times mr-2"></i>
          Clear All Filters
        </Button>
      )}
    </div>
  );

  // Render error state
  const renderErrorState = () => (
    <div className="text-center py-16" data-testid="error-state">
      <div className="w-20 h-20 mx-auto mb-6 bg-red-50 rounded-full flex items-center justify-center">
        <i className="fas fa-exclamation-triangle text-3xl text-red-400"></i>
      </div>
      <h3 className="text-xl font-semibold text-gray-700 mb-2">Something went wrong</h3>
      <p className="text-gray-500 mb-6 max-w-md mx-auto">{error}</p>
      <Button onClick={handleSearch} data-testid="retry-btn">
        <i className="fas fa-redo mr-2"></i>
        Try Again
      </Button>
    </div>
  );

  // Render initial state (before any search)
  const renderInitialState = () => (
    <div className="text-center py-16" data-testid="initial-state">
      <div className="w-24 h-24 mx-auto mb-6 bg-emerald-50 rounded-full flex items-center justify-center">
        <i className="fas fa-search text-4xl text-emerald-500"></i>
      </div>
      <h3 className="text-2xl font-semibold text-gray-700 mb-2">Find Your Perfect Property</h3>
      <p className="text-gray-500 mb-6 max-w-md mx-auto">
        Use the filters above to search for properties, or click the button below to see all available listings.
      </p>
      <Button 
        onClick={handleSearch}
        className="bg-emerald-600 hover:bg-emerald-700"
        data-testid="search-all-btn"
      >
        <i className="fas fa-search mr-2"></i>
        Search All Properties
      </Button>
    </div>
  );

  return (
    <main className="min-h-screen bg-gray-50">
      <Helmet>
        <title>{pageTitle}</title>
        <meta name="description" content={`Browse ${totalResults || 'thousands of'} properties in South Africa. Find houses, apartments, and land for sale or rent.`} />
      </Helmet>

      {/* Search Header */}
      <div className="bg-white shadow-sm sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-4 py-4">
          {/* Quick Search Bar */}
          <div className="flex flex-col sm:flex-row gap-3 mb-4">
            <div className="flex-1 relative">
              <i className="fas fa-search absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400"></i>
              <Input
                type="text"
                placeholder="Search by location, suburb, or city..."
                value={searchQuery}
                onChange={handleQuickSearch}
                onKeyDown={handleQuickSearchKeyDown}
                className="pl-10 h-12"
                data-testid="search-input"
              />
            </div>
            <Button 
              onClick={handleSearch}
              className="bg-emerald-600 hover:bg-emerald-700 h-12 px-8"
              disabled={loading}
              data-testid="search-btn"
            >
              {loading ? (
                <>
                  <i className="fas fa-spinner fa-spin mr-2"></i>
                  Searching...
                </>
              ) : (
                <>
                  <i className="fas fa-search mr-2"></i>
                  Search
                </>
              )}
            </Button>
          </div>

          {/* Advanced Filters */}
          <AdvancedFilters 
            filters={filters} 
            onFiltersChange={handleFiltersChange}
            onSearch={handleSearch}
            isMobile={isMobile}
          />

          {/* Active Filters Chips */}
          {activeFilterCount > 0 && (
            <div className="mt-3 pt-3 border-t">
              <FilterChips
                filters={filters}
                onRemoveFilter={handleRemoveFilter}
                onClearAll={handleClearFilters}
              />
            </div>
          )}
        </div>
      </div>

      {/* Results Section */}
      <div className="max-w-7xl mx-auto px-4 py-6">
        {/* Results Header */}
        {hasSearched && !error && (
          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3 mb-6">
            <div>
              <h1 className="text-xl md:text-2xl font-semibold text-gray-800">
                {loading ? 'Searching...' : (
                  totalResults > 0 
                    ? `${totalResults.toLocaleString()} ${totalResults === 1 ? 'Property' : 'Properties'} Found`
                    : 'No Properties Found'
                )}
              </h1>
              {!loading && totalResults > 0 && (
                <p className="text-sm text-gray-500 mt-1">
                  Showing {((currentPage - 1) * ITEMS_PER_PAGE) + 1} - {Math.min(currentPage * ITEMS_PER_PAGE, totalResults)} of {totalResults}
                </p>
              )}
            </div>
            
            {totalResults > 0 && (
              <Select 
                value={filters.sortBy} 
                onValueChange={(value) => {
                  const newFilters = { ...filters, sortBy: value };
                  setFilters(newFilters);
                  searchListings(1, newFilters);
                }}
              >
                <SelectTrigger className="w-full sm:w-48" data-testid="sort-select">
                  <SelectValue placeholder="Sort by" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="newest">Newest First</SelectItem>
                  <SelectItem value="oldest">Oldest First</SelectItem>
                  <SelectItem value="price_asc">Price: Low to High</SelectItem>
                  <SelectItem value="price_desc">Price: High to Low</SelectItem>
                  <SelectItem value="beds_desc">Most Bedrooms</SelectItem>
                </SelectContent>
              </Select>
            )}
          </div>
        )}

        {/* Content States */}
        {initialLoad ? (
          renderSkeletons()
        ) : error ? (
          renderErrorState()
        ) : loading ? (
          renderSkeletons()
        ) : !hasSearched ? (
          renderInitialState()
        ) : listings.length === 0 ? (
          renderEmptyState()
        ) : (
          <>
            {/* Property Grid */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6" data-testid="property-grid">
              {listings.map((listing) => (
                <Card 
                  key={listing.id} 
                  className="overflow-hidden hover:shadow-lg transition-shadow cursor-pointer group"
                  onClick={() => handleListingClick(listing)}
                  data-testid="property-card"
                >
                  {/* Image */}
                  <div className="relative aspect-video overflow-hidden bg-gray-100">
                    {listing.images && listing.images.length > 0 ? (
                      <img 
                        src={typeof listing.images[0] === 'string' ? listing.images[0] : listing.images[0]?.thumbnail || listing.images[0]?.full}
                        alt={listing.title}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                        loading="lazy"
                      />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center">
                        <i className="fas fa-home text-4xl text-gray-300"></i>
                      </div>
                    )}
                    
                    {/* Badges */}
                    <div className="absolute top-2 left-2 flex gap-1">
                      <Badge className={listing.type === 'sale' ? 'bg-emerald-600' : 'bg-blue-600'}>
                        {listing.type === 'sale' ? 'For Sale' : 'To Rent'}
                      </Badge>
                    </div>
                    
                    {listing.images && listing.images.length > 1 && (
                      <Badge className="absolute top-2 right-2 bg-black/70">
                        <i className="fas fa-images mr-1"></i>
                        {listing.images.length}
                      </Badge>
                    )}
                  </div>
                  
                  {/* Content */}
                  <CardContent className="p-4">
                    <h3 className="font-semibold text-gray-800 line-clamp-1 mb-1">
                      {listing.title}
                    </h3>
                    <p className="text-xl font-bold text-emerald-600 mb-2">
                      {formatPrice(listing.price)}
                      {listing.type === 'rental' && <span className="text-sm font-normal text-gray-500">/month</span>}
                    </p>
                    
                    {/* Specs */}
                    <div className="flex items-center gap-3 text-sm text-gray-600 mb-2">
                      {listing.specifications?.beds && (
                        <span className="flex items-center gap-1">
                          <i className="fas fa-bed text-gray-400"></i>
                          {listing.specifications.beds}
                        </span>
                      )}
                      {listing.specifications?.baths && (
                        <span className="flex items-center gap-1">
                          <i className="fas fa-bath text-gray-400"></i>
                          {listing.specifications.baths}
                        </span>
                      )}
                      {listing.specifications?.floorSize && (
                        <span className="flex items-center gap-1">
                          <i className="fas fa-expand text-gray-400"></i>
                          {listing.specifications.floorSize}m²
                        </span>
                      )}
                    </div>
                    
                    {/* Location */}
                    <p className="text-sm text-gray-500 line-clamp-1">
                      <i className="fas fa-map-marker-alt mr-1 text-gray-400"></i>
                      {listing.propertyAddress}
                    </p>
                  </CardContent>
                </Card>
              ))}
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="flex justify-center items-center gap-2 mt-8" data-testid="pagination">
                <Button
                  variant="outline"
                  onClick={() => handlePageChange(currentPage - 1)}
                  disabled={currentPage === 1}
                >
                  <i className="fas fa-chevron-left"></i>
                </Button>
                
                {/* Page numbers */}
                <div className="flex items-center gap-1">
                  {currentPage > 2 && (
                    <>
                      <Button variant="outline" onClick={() => handlePageChange(1)}>1</Button>
                      {currentPage > 3 && <span className="px-2 text-gray-400">...</span>}
                    </>
                  )}
                  
                  {[currentPage - 1, currentPage, currentPage + 1]
                    .filter(p => p >= 1 && p <= totalPages)
                    .map(page => (
                      <Button
                        key={page}
                        variant={page === currentPage ? 'default' : 'outline'}
                        onClick={() => handlePageChange(page)}
                        className={page === currentPage ? 'bg-emerald-600 hover:bg-emerald-700' : ''}
                      >
                        {page}
                      </Button>
                    ))
                  }
                  
                  {currentPage < totalPages - 1 && (
                    <>
                      {currentPage < totalPages - 2 && <span className="px-2 text-gray-400">...</span>}
                      <Button variant="outline" onClick={() => handlePageChange(totalPages)}>{totalPages}</Button>
                    </>
                  )}
                </div>
                
                <Button
                  variant="outline"
                  onClick={() => handlePageChange(currentPage + 1)}
                  disabled={currentPage === totalPages}
                >
                  <i className="fas fa-chevron-right"></i>
                </Button>
              </div>
            )}
          </>
        )}
      </div>

      {/* Property Detail Modal */}
      <Dialog open={showDetailModal} onOpenChange={setShowDetailModal}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-hidden p-0">
          <ScrollArea className="max-h-[90vh]">
            {selectedListing && (
              <div>
                {/* Image Gallery */}
                <div className="relative aspect-video bg-gray-100">
                  {selectedListing.images && selectedListing.images.length > 0 ? (
                    <>
                      <img 
                        src={typeof selectedListing.images[currentImageIndex] === 'string' 
                          ? selectedListing.images[currentImageIndex] 
                          : selectedListing.images[currentImageIndex]?.full || selectedListing.images[currentImageIndex]?.medium}
                        alt={`${selectedListing.title} - Image ${currentImageIndex + 1}`}
                        className="w-full h-full object-cover"
                      />
                      
                      {selectedListing.images.length > 1 && (
                        <>
                          <button
                            onClick={(e) => { e.stopPropagation(); handlePrevImage(); }}
                            className="absolute left-2 top-1/2 -translate-y-1/2 bg-black/50 hover:bg-black/70 text-white p-3 rounded-full"
                          >
                            <i className="fas fa-chevron-left"></i>
                          </button>
                          <button
                            onClick={(e) => { e.stopPropagation(); handleNextImage(); }}
                            className="absolute right-2 top-1/2 -translate-y-1/2 bg-black/50 hover:bg-black/70 text-white p-3 rounded-full"
                          >
                            <i className="fas fa-chevron-right"></i>
                          </button>
                          <div className="absolute bottom-4 left-1/2 -translate-x-1/2 bg-black/50 text-white px-3 py-1 rounded-full text-sm">
                            {currentImageIndex + 1} / {selectedListing.images.length}
                          </div>
                        </>
                      )}
                    </>
                  ) : (
                    <div className="w-full h-full flex items-center justify-center">
                      <i className="fas fa-home text-6xl text-gray-300"></i>
                    </div>
                  )}
                </div>

                {/* Property Details */}
                <div className="p-6">
                  <DialogHeader className="mb-4">
                    <div className="flex justify-between items-start">
                      <div>
                        <Badge className={selectedListing.type === 'sale' ? 'bg-emerald-600 mb-2' : 'bg-blue-600 mb-2'}>
                          {selectedListing.type === 'sale' ? 'For Sale' : 'To Rent'}
                        </Badge>
                        <DialogTitle className="text-2xl">{selectedListing.title}</DialogTitle>
                        <p className="text-gray-500 mt-1">
                          <i className="fas fa-map-marker-alt mr-1"></i>
                          {selectedListing.propertyAddress}
                        </p>
                      </div>
                      <div className="text-right">
                        <p className="text-3xl font-bold text-emerald-600">
                          {formatPrice(selectedListing.price)}
                        </p>
                        {selectedListing.type === 'rental' && (
                          <span className="text-gray-500">per month</span>
                        )}
                      </div>
                    </div>
                  </DialogHeader>

                  {/* Specs Grid */}
                  {selectedListing.specifications && (
                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 p-4 bg-gray-50 rounded-lg mb-6">
                      {selectedListing.specifications.beds && (
                        <div className="text-center">
                          <i className="fas fa-bed text-2xl text-emerald-600 mb-1"></i>
                          <p className="font-semibold">{selectedListing.specifications.beds}</p>
                          <p className="text-xs text-gray-500">Bedrooms</p>
                        </div>
                      )}
                      {selectedListing.specifications.baths && (
                        <div className="text-center">
                          <i className="fas fa-bath text-2xl text-emerald-600 mb-1"></i>
                          <p className="font-semibold">{selectedListing.specifications.baths}</p>
                          <p className="text-xs text-gray-500">Bathrooms</p>
                        </div>
                      )}
                      {selectedListing.specifications.floorSize && (
                        <div className="text-center">
                          <i className="fas fa-expand text-2xl text-emerald-600 mb-1"></i>
                          <p className="font-semibold">{selectedListing.specifications.floorSize}m²</p>
                          <p className="text-xs text-gray-500">Floor Size</p>
                        </div>
                      )}
                      {selectedListing.specifications.propertySize && (
                        <div className="text-center">
                          <i className="fas fa-ruler-combined text-2xl text-emerald-600 mb-1"></i>
                          <p className="font-semibold">{selectedListing.specifications.propertySize}m²</p>
                          <p className="text-xs text-gray-500">Property Size</p>
                        </div>
                      )}
                    </div>
                  )}

                  {/* Description */}
                  {selectedListing.description && (
                    <div className="mb-6">
                      <h3 className="font-semibold mb-2">Description</h3>
                      <p className="text-gray-600 whitespace-pre-wrap">{selectedListing.description}</p>
                    </div>
                  )}

                  {/* Contact Buttons */}
                  <div className="flex flex-wrap gap-3">
                    {selectedListing.whatsappNumber && (
                      <Button 
                        className="flex-1 bg-green-600 hover:bg-green-700"
                        onClick={() => window.open(`https://wa.me/${selectedListing.whatsappNumber.replace(/\D/g, '')}?text=Hi, I'm interested in: ${selectedListing.title}`, '_blank')}
                      >
                        <i className="fab fa-whatsapp mr-2"></i>
                        WhatsApp
                      </Button>
                    )}
                    {selectedListing.contactNumber && (
                      <Button 
                        variant="outline"
                        className="flex-1"
                        onClick={() => window.open(`tel:${selectedListing.contactNumber}`, '_blank')}
                      >
                        <i className="fas fa-phone mr-2"></i>
                        Call
                      </Button>
                    )}
                    <Button 
                      variant="outline"
                      onClick={() => setShowReportModal(true)}
                    >
                      <i className="fas fa-flag mr-2"></i>
                      Report
                    </Button>
                  </div>
                </div>
              </div>
            )}
          </ScrollArea>
        </DialogContent>
      </Dialog>

      {/* Report Modal */}
      <Dialog open={showReportModal} onOpenChange={setShowReportModal}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Report Listing</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Reason *</Label>
              <Select 
                value={reportData.reason} 
                onValueChange={(value) => setReportData({ ...reportData, reason: value })}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select a reason" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="spam">Spam or Scam</SelectItem>
                  <SelectItem value="inaccurate">Inaccurate Information</SelectItem>
                  <SelectItem value="inappropriate">Inappropriate Content</SelectItem>
                  <SelectItem value="duplicate">Duplicate Listing</SelectItem>
                  <SelectItem value="other">Other</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Your Email *</Label>
              <Input
                type="email"
                value={reportData.reporterEmail}
                onChange={(e) => setReportData({ ...reportData, reporterEmail: e.target.value })}
                placeholder="your@email.com"
              />
            </div>
            <div>
              <Label>Description</Label>
              <Textarea
                value={reportData.description}
                onChange={(e) => setReportData({ ...reportData, description: e.target.value })}
                placeholder="Provide more details..."
                rows={3}
              />
            </div>
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => setShowReportModal(false)}>
                Cancel
              </Button>
              <Button onClick={handleReportSubmit} className="bg-red-600 hover:bg-red-700">
                Submit Report
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </main>
  );
};

export default PropertySearch;
